import { ReactIcons, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";
import { Touchable } from "@kalyzee/kast-web-sdk/web-components";
import { PlayerActionItem, PlayerWorkBox, PlayerWorkBoxOverlayContent, PlayerWorkBoxProps } from "./PlayerWorkBox";
import { MeetingSession, MeetingSessionHighlightingState, MeetingSessionMedia, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isSuperUser, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback } from "react";
import { getNormalUser } from "../../helpers/user";
import { LayoutType } from "../../interfaces/ui";

import styles from "./MasterPlayerWorkBox.module.css";

export interface MasterPlayerWorkBoxProps {
  meetingAPI: MeetingContainerAPI;
  children: React.ReactNode;
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
  overlayMode?: PlayerWorkBoxProps["overlayMode"];
  isSmall?: boolean;
  isPlayingSound?: boolean;
  /**
   * Either a typed overlay variant (rendered into a built-in component) or a
   * free-form ReactNode for fully custom overlays.
   */
  overlayContent?: PlayerWorkBoxOverlayContent;
  /** Non-selected secondary (strip) player: hides the source info indicators. */
  isSidePlayer?: boolean;
  /** Interactive minimap/crop feature (owned entirely by PlayerWorkBox). */
  minimap?: PlayerWorkBoxProps["minimap"];
  topbarActions?: PlayerActionItem[];
  mediaButtons?: boolean;
  localStream?: boolean;
  extraHeaderContent?: React.ReactNode;
  className?: string;
  topBarClassName?: string;
  boxProps?: React.HTMLAttributes<HTMLDivElement>;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const MasterPlayerWorkBox = React.memo(({
  meetingAPI,
  children,
  session,
  mediaSession,
  overlayMode,
  overlayContent,
  isSidePlayer,
  minimap,
  topbarActions,
  isSmall,
  isPlayingSound,
  mediaButtons,
  localStream,
  extraHeaderContent,
  className,
  topBarClassName,
  boxProps,
  containerRef,
}: MasterPlayerWorkBoxProps) => {
  const [meetingOngoing] = useMeetingSelector(meetingAPI, "meeting", meeting => meeting?.state === MeetingState.RUNNING);
  const [sessionMe] = useMeetingSelector(meetingAPI, "session");
  const isMe = sessionMe?.id === session.id;
  const { displayName, avatar } = session.user ? getNormalUser(session.user) : { displayName: undefined, avatar: undefined };

  const getTopBarLabel = () => {
    if (isMe) return "Vous";
    return displayName ?? "Utilisateur";
  };

  const getTobBarLabelInfo = () => {
    if (isSidePlayer) return undefined;
    let label = mediaSession.extra?.label ?? "webcam";
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const enableControls = mediaButtons && (isMe || isSuperUser(sessionMe));

  const videoEnabled = mediaSession.video?.enabled ?? false;
  const audioEnabled = mediaSession.audio?.enabled ?? false;

  const onVideo = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      video: {
        enabled: !videoEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, videoEnabled]);

  const onAudio = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      audio: {
        enabled: !audioEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, audioEnabled]);

  return (
    <PlayerWorkBox
      label={getTopBarLabel()}
      userDisplayName={displayName}
      avatar={avatar}
      userId={session.user?.id}
      labelInfo={getTobBarLabelInfo()}
      overlayMode={overlayMode}
      overlayContent={overlayContent}
      topbarActions={topbarActions}
      extraHeaderContent={extraHeaderContent}
      isScreenSource={mediaSession.extra?.type === "screen"}
      isSidePlayer={isSidePlayer}
      minimap={minimap}
      className={className}
      topBarClassName={topBarClassName}
      video={videoEnabled}
      audio={audioEnabled}
      onChangeVideo={enableControls ? onVideo : undefined}
      onChangeAudio={enableControls ? onAudio : undefined}
      isMe={isMe}
      isSmall={isSmall}
      isHighlighted={session.highlighting === MeetingSessionHighlightingState.ONGOING}
      isPlayingSound={isPlayingSound}
      boxProps={boxProps}
      containerRef={containerRef}
      idleAnimation={!meetingOngoing}
      mediaButtons={mediaButtons}
      localStream={localStream}
    >
      {children}
    </PlayerWorkBox>
  );
});
